import { useCallback, useEffect, useState } from 'react'

export interface UseMultiVideoUploaderOptions {
  maxSizeMB?: number
  maxFiles?: number
  allowedTypes?: string[]
}

type Preview = {
  url: string
  id: number
}

export const useMultiVideoUploader = (options: UseMultiVideoUploaderOptions = {}) => {
  const {
    maxSizeMB = 25,
    maxFiles = 5,
    allowedTypes = ['video/mp4'],
  } = options

  const [previews, setPreviews] = useState<Preview[]>([])
  const [files, setFiles] = useState<File[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    return () => {
      previews.forEach(preview => URL.revokeObjectURL(preview.url))
    }
  }, [previews])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files
      if (!selectedFiles || selectedFiles.length === 0) return

      const totalFiles = files.length + selectedFiles.length
      if (totalFiles > maxFiles) {
        setError(
          `No puedes subir más de ${maxFiles} videos. Has intentado subir ${selectedFiles.length} pero ya tienes ${files.length}.`,
        )
        e.target.value = ''
        return
      }

      setIsProcessing(true)
      setError(null)

      const newFiles: File[] = []
      const newPreviews: Preview[] = []

      Array.from(selectedFiles).forEach((file, index) => {
        if (!allowedTypes.includes(file.type)) {
          setError(`El archivo '${file.name}' no es un video válido (${allowedTypes.join(', ')}).`)
          return
        }

        const maxBytes = maxSizeMB * 1024 * 1024
        if (file.size > maxBytes) {
          setError(`El archivo '${file.name}' supera el tamaño máximo de ${maxSizeMB}MB.`)
          return
        }

        newFiles.push(file)
        newPreviews.push({
          url: URL.createObjectURL(file),
          id: Date.now() + index,
        })
      })

      const updatedFiles = [...files, ...newFiles]
      setFiles(updatedFiles)
      setPreviews(prev => [...prev, ...newPreviews])

      setIsProcessing(false)
      e.target.value = ''
    },
    [allowedTypes, files, maxFiles, maxSizeMB],
  )

  const removeVideo = useCallback((index: number) => {
    setFiles(current => current.filter((_, i) => i !== index))
    setPreviews(current => {
      const updated = [...current]
      const removed = updated.splice(index, 1)
      if (removed[0]) {
        URL.revokeObjectURL(removed[0].url)
      }
      return updated
    })
  }, [])

  const reset = useCallback(() => {
    previews.forEach(preview => URL.revokeObjectURL(preview.url))
    setFiles([])
    setPreviews([])
    setError(null)
  }, [previews])

  return {
    files,
    previews,
    isProcessing,
    error,
    handleFileChange,
    removeVideo,
    reset,
  }
}

